import { Component, Input, Output, EventEmitter, OnInit, OnChanges } from '@angular/core';
import { finalize } from 'rxjs/operators';
import { TextInputComponent } from '../text-input/text-input.component';
import { SelectOption } from '../../model/select.option';
import { SubmissionsService } from 'src/app/service/submissions.service';
import { SubmissionStatus } from 'src/app/enum/submission.status';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { SubmissionWithdrawalComponent } from 'src/app/component/submission-withdrawal/submission-withdrawal.component';
import { Submission } from 'src/app/model/paper';

/**
 * `app-status-select` — versão LEVE do seletor de status (análogo a select-input-group):
 * botão-chip + app-popover + app-status-menu, SEM mat-select. Preserva a lógica smart do
 * `app-select-input-icon`: persiste via editSubmission, abre o diálogo de retirada no
 * WITHDRAWN, emite selectionChange e mostra spinner "Salvando…". Fonte da verdade = o
 * reactive form control (formGroupController[name]), igual ao componente atual.
 */
@Component({
  selector: 'app-status-select',
  templateUrl: './status-select.component.html',
  styleUrls: ['./status-select.component.scss'],
})
export class StatusSelectComponent extends TextInputComponent implements OnInit, OnChanges {
  @Input() options: SelectOption[];
  @Input() indices = { internal: 'id', public: 'value' };
  /** Mapa por status: { [valor]: { fontSet, fontIcon, color } }. */
  @Input() icons: { [key: string]: { fontSet: string; fontIcon: string; color: string } } = {};
  @Input() prependPublic = '';
  @Input() readonly submission: Submission;
  @Input() includeNoneOption = false;
  @Input() compact = false;
  /** Clareia o chip (camada branca) — segue o toggle "Colorir linhas com status". */
  @Input() lighten = true;

  /** Modo somente-leitura: mostra o pill (com ícone + rótulo) mas SEM caret e
   *  sem popover — o clique não abre o menu. Usado no "Status de publicação",
   *  onde decisão editorial não pertence ao contexto. */
  @Input() readOnly = false;

  @Output() selectionChange = new EventEmitter<{ submissionID: number; value: string }>();
  @Output() closed = new EventEmitter<boolean>();

  public saving = false;
  public value: string;
  public currentIcon: { fontSet: string; fontIcon: string; color: string } | null = null;

  constructor(
    private submissionsService: SubmissionsService,
    private dialog: MatDialog,
  ) {
    super();
  }

  /** Id do status atual (fonte da verdade = reactive form control). */
  get currentId(): any {
    return this.formGroupController?.get(this.name)?.value;
  }

  /** Classe (lowercase) p/ as cores per-status dos chips globais (&.active/.accepted/…). */
  get statusClass(): string {
    const v = this.currentId;
    return v ? v.toString().toLowerCase() : '';
  }

  ngOnChanges(): void { this.ngOnInit(); }

  ngOnInit(): void {
    this.refreshDisplay();
  }

  private refreshDisplay(): void {
    const v = this.currentId;
    this.value = this.getDisplayValue(v);
    this.currentIcon = v ? (this.icons[v] || null) : null;
  }

  private getDisplayValue(value: any): string {
    let display = '';
    (this.options || []).forEach((option: SelectOption) => {
      if (option[this.indices.internal] === value) {
        display = `${this.prependPublic}${option[this.indices.public]}`;
      }
    });
    return display;
  }

  /** Chamado pelo app-status-menu ao escolher um status (ou "nenhum" → null). */
  public onSelect(value: any): void {
    if (value === SubmissionStatus.WITHDRAWN && this.submission) {
      this.openWithdrawDialog();   // não persiste direto; o diálogo confirma a retirada
      return;
    }
    this.formGroupController.get(this.name)?.setValue(value);
    this.refreshDisplay();

    // Sem submissão (ex.: tabela v1, que não passa [submission]) → só atualiza o display,
    // sem persistir (o componente atual estourava aqui; mantemos a paridade sem o erro).
    if (!this.submission) { return; }

    this.saving = true;
    this.submissionsService.editSubmission({ status: value }, this.submission.id)
      .pipe(finalize(() => this.saving = false))
      .subscribe(() => {
        // Sem notificação de sucesso: o spinner "Salvando…" no chip já sinaliza.
        this.selectionChange.emit({ submissionID: this.submission.id, value });
      });
  }

  private openWithdrawDialog(): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = false;
    dialogConfig.data = { submission: this.submission, submissions: null, previousStatus: this.submission.status };

    const dialogRef = this.dialog.open(SubmissionWithdrawalComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(response => {
      if (response) {
        this.formGroupController.get(this.name)?.setValue(SubmissionStatus.WITHDRAWN);
        this.refreshDisplay();
        this.selectionChange.emit({ submissionID: this.submission.id, value: SubmissionStatus.WITHDRAWN });
        return;
      }
      // cancelado → mantém o status anterior
      this.selectionChange.emit({ submissionID: this.submission.id, value: this.submission.status });
    });
  }

  public onClosed(): void {
    this.closed.emit(true);
  }
}
